import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { lazy, Suspense, useLayoutEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";

import PublicLayout from "@/components/PublicLayout";
import HomePage from "@/pages/HomePage";
import SectionPage from "@/pages/SectionPage";
import ArticlePage from "@/pages/ArticlePage";
import SearchPage from "@/pages/SearchPage";
import AboutPage from "@/pages/AboutPage";
import LoginPage from "@/pages/LoginPage";
import ResetPage from "@/pages/ResetPage";
import ForceChangePasswordPage from "@/pages/ForceChangePasswordPage";
import ContactPage from "@/pages/ContactPage";
import PrivacyPage from "@/pages/PrivacyPage";
import CorrectionsPage from "@/pages/CorrectionsPage";

import SavedPage from "@/pages/SavedPage";
import ReelsPage from "@/pages/ReelsPage";
import VideoPage from "@/pages/VideoPage";
import NotFoundPage from "@/pages/NotFoundPage";

// Admin panel, editor and charts are split out of the visitor bundle and fetched only when an admin opens them.
const AdminMessages = lazy(() => import("@/pages/admin/AdminMessages"));
const AdminPending = lazy(() => import("@/pages/admin/AdminPending"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminArticles = lazy(() => import("@/pages/admin/AdminArticles"));
const ArticleEditor = lazy(() => import("@/pages/admin/ArticleEditor"));
const AdminSections = lazy(() => import("@/pages/admin/AdminSections"));
const AdminTickers = lazy(() => import("@/pages/admin/AdminTickers"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminReadiness = lazy(() => import("@/pages/admin/AdminReadiness"));
const AdminVersions = lazy(() => import("@/pages/admin/AdminVersions"));
const AdminPublishingCenter = lazy(() => import("@/pages/admin/AdminPublishingCenter"));
const AdminMediaLibrary = lazy(() => import("@/pages/admin/AdminMediaLibrary"));
const AdminPushPerformance = lazy(() => import("@/pages/admin/AdminPushPerformance"));
const AdminAgent = lazy(() => import("@/pages/admin/AdminAgent"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminAccount = lazy(() => import("@/pages/admin/AdminAccount"));
const AdminTelegram = lazy(() => import("@/pages/admin/AdminTelegram"));
const AdminPush = lazy(() => import("@/pages/admin/AdminPush"));
const AdminTranslations = lazy(() => import("@/pages/admin/AdminTranslations"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AdminStats = lazy(() => import("@/pages/admin/AdminStats"));
const AdminIndexAssistant = lazy(() => import("@/pages/admin/AdminIndexAssistant"));
const AdminActivity = lazy(() => import("@/pages/admin/AdminActivity"));
const AdminGsc = lazy(() => import("@/pages/admin/AdminGsc"));
const AdminIndexing = lazy(() => import("@/pages/admin/AdminIndexing"));
const AdminCountries = lazy(() => import("@/pages/admin/AdminCountries"));
const AdminEngagement = lazy(() => import("@/pages/admin/AdminEngagement"));
const AdminMediaHealth = lazy(() => import("@/pages/admin/AdminMediaHealth"));
const AdminReels = lazy(() => import("@/pages/admin/AdminReels"));
const AdminFallback = () => <div className="p-10 text-center text-sm text-gray-500" data-testid="admin-loading">جارٍ تحميل لوحة التحكم…</div>;

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading || user === null) return <div className="p-10 text-center">جارٍ التحقق...</div>;
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

// Scroll manager: new pages start at the top; on back/forward we restore the saved
// reading position ourselves once the content is tall enough (the browser's native
// restore fires before the news loads and scroll-anchoring then drifts to the footer).
if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
const SCROLL_KEY = "scroll-positions";
const readPositions = () => { try { return JSON.parse(sessionStorage.getItem(SCROLL_KEY) || "{}"); } catch { return {}; } };
function ScrollToTopOnPush() {
    const location = useLocation();
    const navType = useNavigationType();
    // Layout effect: the leaving page's position is saved BEFORE the browser clamps scrollY to the shorter new page.
    useLayoutEffect(() => {
        const key = location.key;
        let restoring = false;
        const save = () => { if (restoring) return; const p = readPositions(); p[key] = window.scrollY; try { sessionStorage.setItem(SCROLL_KEY, JSON.stringify(p)); } catch {} };
        window.addEventListener("scroll", save, { passive: true });
        if (navType === "POP") {
            const target = readPositions()[key] || 0;
            let tries = 0;
            restoring = true;
            const tick = () => {
                const maxY = document.documentElement.scrollHeight - window.innerHeight;
                window.scrollTo(0, Math.min(target, Math.max(maxY, 0)));
                if (maxY >= target || tries > 100) { restoring = false; return; }
                tries += 1; setTimeout(tick, 50);
            };
            tick();
        } else {
            window.scrollTo(0, 0);
        }
        return () => { restoring = false; save(); window.removeEventListener("scroll", save); };
    }, [location.key, navType]);
    return null;
}

export default function App() {
    // Guarantee RTL + Arabic at runtime, even if any component tries to
    // temporarily flip the direction. This is our defensive floor so a
    // future admin/theme toggle can never leave the site in LTR by mistake.
    if (typeof document !== "undefined") {
        const h = document.documentElement;
        if (h.getAttribute("lang") !== "ar") h.setAttribute("lang", "ar");
        if (h.getAttribute("dir") !== "rtl") h.setAttribute("dir", "rtl");
    }
    return (
        <ThemeProvider>
            <AuthProvider>
                <BrowserRouter>
                    <ScrollToTopOnPush />
                    <Toaster position="top-center" richColors closeButton dir="rtl" />
                    <Routes>
                        <Route element={<PublicLayout key="ar" lang="ar" />}>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/section/gaza" element={<Navigate to="/section/palestine" replace />} />
                            <Route path="/section/breaking" element={<Navigate to="/" replace />} />
                            <Route path="/section/:slug" element={<SectionPage />} />
                            <Route path="/article/:slug" element={<ArticlePage />} />
                            <Route path="/search" element={<SearchPage />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/privacy" element={<PrivacyPage />} />
                            <Route path="/saved" element={<SavedPage />} />
                            <Route path="/reels" element={<ReelsPage />} />
                            <Route path="/video/:slug" element={<VideoPage />} />
                            <Route path="/corrections" element={<CorrectionsPage />} />
                            <Route path="*" element={<NotFoundPage />} />
                        </Route>
                        {["en", "es", "nl"].map((L) => (
                        <Route key={L} path={`/${L}`} element={<PublicLayout key={L} lang={L} />}>
                            <Route index element={<HomePage />} />
                            <Route path="section/gaza" element={<Navigate to={`/${L}/section/palestine`} replace />} />
                            <Route path="section/breaking" element={<Navigate to="/" replace />} />
                            <Route path="section/:slug" element={<SectionPage />} />
                            <Route path="article/:slug" element={<ArticlePage />} />
                            <Route path="search" element={<SearchPage />} />
                            <Route path="about" element={<AboutPage />} />
                            <Route path="contact" element={<ContactPage />} />
                            <Route path="privacy" element={<PrivacyPage />} />
                            <Route path="saved" element={<SavedPage />} />
                            <Route path="reels" element={<ReelsPage />} />
                            <Route path="video/:slug" element={<VideoPage />} />
                            <Route path="corrections" element={<CorrectionsPage />} />
                            <Route path="*" element={<NotFoundPage />} />
                        </Route>
                        ))}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/reset" element={<ResetPage />} />
                        <Route path="/force-change-password" element={<ProtectedRoute><ForceChangePasswordPage /></ProtectedRoute>} />
                        <Route path="/admin" element={<ProtectedRoute><Suspense fallback={<AdminFallback />}><AdminLayout /></Suspense></ProtectedRoute>}>
                            <Route index element={<AdminDashboard />} />
                            <Route path="articles" element={<AdminArticles />} />
                            <Route path="articles/new" element={<ArticleEditor />} />
                            <Route path="articles/:id" element={<ArticleEditor />} />
                            <Route path="sections" element={<AdminSections />} />
                            <Route path="tickers" element={<AdminTickers />} />
                            <Route path="users" element={<AdminUsers />} />
                            <Route path="messages" element={<AdminMessages />} />
                            <Route path="pending" element={<AdminPending />} />
                            <Route path="account" element={<AdminAccount />} />
                            <Route path="telegram" element={<AdminTelegram />} />
                            <Route path="push" element={<AdminPush />} />
                            <Route path="translations" element={<AdminTranslations />} />
                            <Route path="analytics" element={<AdminAnalytics />} />
                            <Route path="stats" element={<AdminStats />} />
                            <Route path="activity" element={<AdminActivity />} />
                            <Route path="google" element={<AdminGsc />} />
                            <Route path="indexing" element={<AdminIndexing />} />
                            <Route path="index-assistant" element={<AdminIndexAssistant />} />
                            <Route path="countries" element={<AdminCountries />} />
                            <Route path="engagement" element={<AdminEngagement />} />
                            <Route path="media-health" element={<AdminMediaHealth />} />
                            <Route path="reels" element={<AdminReels />} />
                            <Route path="readiness" element={<AdminReadiness />} />
                            <Route path="versions" element={<AdminVersions />} />
                            <Route path="publishing" element={<AdminPublishingCenter />} />
                            <Route path="media-library" element={<AdminMediaLibrary />} />
                            <Route path="push-performance" element={<AdminPushPerformance />} />
                            <Route path="agent" element={<AdminAgent />} />
                            <Route path="settings" element={<AdminSettings />} />
                        </Route>
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </ThemeProvider>
    );
}
